// ============================================
// GESTÃO DE USUÁRIOS DO SISTEMA
// Painel administrativo para gerenciar acessos
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Tooltip,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Autocomplete,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LockReset as LockResetIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  AdminPanelSettings as AdminIcon,
  VerifiedUser,
  MedicalServices as ProfessionalRoleIcon,
  SupportAgent as ReceptionistIcon,
  AccountBalance as FinancialIcon,
  AssistantPhoto as AssistantIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  People as PeopleIcon,
  PersonOff as PersonOffIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import UserUnitsDialog from '../components/admin/UserUnitsDialog';
import BusinessIcon from '@mui/icons-material/Business';
import {
  SystemRole,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  ROLE_COLORS,
  getAllRoles,
  getRoleLabel,
} from '../config/permissions';

// ============================================
// TIPOS
// ============================================

interface SystemUser {
  id: string;
  email: string;
  full_name: string;
  role: SystemRole;
  professional_id: string | null;
  avatar_url: string | null;
  phone: string | null;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  role_label?: string;
  professional?: {
    id: string;
    full_name: string;
    specialty: string;
    registration_number: string;
    registration_type: string;
  } | null;
}

interface UserStats {
  total: number;
  active: number;
  inactive: number;
  by_role: Record<string, number>;
}

interface Professional {
  id: string;
  full_name: string;
  specialty: string;
  registration_number: string;
  registration_type: string;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const UserManagement: React.FC = () => {
  const { token, user: currentUser } = useAuth();
  
  // State
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);
  const [unitsDialogOpen, setUnitsDialogOpen] = useState(false);
  const [unitsDialogUser, setUnitsDialogUser] = useState<SystemUser | null>(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState('');
  const [activityLogDialog, setActivityLogDialog] = useState(false);
  
  // Form states
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'professional' as SystemRole,
    professional_id: '',
    phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Professionals for linking
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  
  // Activity logs
  const [activityLogs, setActivityLogs] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  
  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  
  // Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUserId, setMenuUserId] = useState('');

  // ============================================
  // API HELPERS
  // ============================================

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  // ============================================
  // FETCH DATA
  // ============================================

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 50 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.is_active = statusFilter;

      const response = await axios.get(`${getApiUrl()}/auth/users`, {
        ...authHeaders(),
        params,
      });

      if (response.data.success) {
        setUsers(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      showSnackbar('Erro ao carregar usuários.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, statusFilter, authHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/auth/users/stats`, authHeaders());
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [authHeaders]);

  const fetchProfessionals = useCallback(async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/clinic/professionals`, authHeaders());
      if (response.data.success) {
        setProfessionals(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
    }
  }, [authHeaders]);

  const fetchActivityLogs = useCallback(async () => {
    try {
      setActivityLoading(true);
      const response = await axios.get(`${getApiUrl()}/auth/activity-logs`, {
        ...authHeaders(),
        params: { limit: 50 },
      });
      if (response.data.success) {
        setActivityLogs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setActivityLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchUsers();
    fetchStats();
    fetchProfessionals();
  }, [fetchUsers, fetchStats, fetchProfessionals]);

  // ============================================
  // HELPERS
  // ============================================

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getRoleIcon = (role: SystemRole): React.ReactElement => {
    const icons: Record<SystemRole, React.ReactElement> = {
      admin: <AdminIcon sx={{ fontSize: 18 }} />,
      admin_master: <VerifiedUser sx={{ fontSize: 18, color: '#c2410c' }} />,
      professional: <ProfessionalRoleIcon sx={{ fontSize: 18 }} />,
      receptionist: <ReceptionistIcon sx={{ fontSize: 18 }} />,
      financial: <FinancialIcon sx={{ fontSize: 18 }} />,
      assistant: <AssistantIcon sx={{ fontSize: 18 }} />,
    };
    return icons[role] || <PersonIcon sx={{ fontSize: 18 }} />;
  };

  const formatDate = (date: string | null) => {
    if (!date) return 'Nunca';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ============================================
  // CRUD OPERATIONS
  // ============================================

  const handleOpenDialog = (user?: SystemUser) => {
    if (user) {
      setEditingUser(user);
      setForm({
        email: user.email,
        password: '',
        full_name: user.full_name,
        role: user.role,
        professional_id: user.professional_id || '',
        phone: user.phone || '',
      });
    } else {
      setEditingUser(null);
      setForm({
        email: '',
        password: '',
        full_name: '',
        role: 'professional',
        professional_id: '',
        phone: '',
      });
    }
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleSave = async () => {
    try {
      if (!form.email || !form.full_name || !form.role) {
        showSnackbar('Preencha todos os campos obrigatórios.', 'error');
        return;
      }

      if (!editingUser && !form.password) {
        showSnackbar('Senha é obrigatória para novos usuários.', 'error');
        return;
      }

      if (!editingUser && form.password.length < 6) {
        showSnackbar('A senha deve ter pelo menos 6 caracteres.', 'error');
        return;
      }

      const payload: any = {
        email: form.email,
        full_name: form.full_name,
        role: form.role,
        professional_id: form.professional_id || null,
        phone: form.phone || null,
      };

      if (!editingUser) {
        payload.password = form.password;
      }

      let createdUser: SystemUser | null = null;
      if (editingUser) {
        await axios.put(`${getApiUrl()}/auth/users/${editingUser.id}`, payload, authHeaders());
        showSnackbar('Usuário atualizado com sucesso!');
      } else {
        const created = await axios.post(`${getApiUrl()}/auth/users`, payload, authHeaders());
        createdUser = (created.data?.data || null) as SystemUser | null;
        showSnackbar('Usuário criado com sucesso! Vincule a uma unidade.');
      }

      handleCloseDialog();
      fetchUsers();
      fetchStats();

      // Após criar um novo usuário, abrir automaticamente o diálogo de unidades
      if (!editingUser && createdUser) {
        setUnitsDialogUser(createdUser);
        setUnitsDialogOpen(true);
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erro ao salvar usuário.';
      showSnackbar(message, 'error');
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    try {
      await axios.patch(
        `${getApiUrl()}/auth/users/${userId}/toggle-status`,
        { is_active: !currentStatus },
        authHeaders()
      );
      showSnackbar(`Usuário ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`);
      fetchUsers();
      fetchStats();
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao alterar status.', 'error');
    }
  };

  const handleResetPassword = async () => {
    try {
      if (!newPassword || newPassword.length < 6) {
        showSnackbar('A senha deve ter pelo menos 6 caracteres.', 'error');
        return;
      }

      await axios.post(
        `${getApiUrl()}/auth/users/${resetPasswordUserId}/reset-password`,
        { new_password: newPassword },
        authHeaders()
      );
      showSnackbar('Senha resetada com sucesso!');
      setResetPasswordDialog(false);
      setNewPassword('');
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao resetar senha.', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${getApiUrl()}/auth/users/${deleteUserId}`, authHeaders());
      showSnackbar('Usuário deletado com sucesso!');
      setDeleteConfirmDialog(false);
      fetchUsers();
      fetchStats();
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Erro ao deletar usuário.', 'error');
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuUserId(userId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId('');
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box sx={{ backgroundColor: 'transparent', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              fontFamily: '"Inter", sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ShieldIcon sx={{ color: '#14b8a6', fontSize: 28 }} />
            Gestão de Usuários
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Gerencie os acessos e perfis dos colaboradores do sistema
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Logs de Atividade">
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => { setActivityLogDialog(true); fetchActivityLogs(); }}
              sx={{
                borderColor: 'rgba(20, 184, 166, 0.3)',
                color: '#0d9488',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#14b8a6',
                  backgroundColor: 'rgba(20, 184, 166, 0.05)',
                },
              }}
            >
              Logs
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(20, 184, 166, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                boxShadow: '0 6px 20px rgba(20, 184, 166, 0.4)',
              },
            }}
          >
            Novo Usuário
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'white', border: '1px solid rgba(20, 184, 166, 0.15)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <PeopleIcon sx={{ fontSize: 20, color: '#14b8a6' }} />
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Total</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'white', border: '1px solid rgba(34, 197, 94, 0.15)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 20, color: '#22c55e' }} />
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Ativos</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>{stats.active}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'white', border: '1px solid rgba(239, 68, 68, 0.15)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <PersonOffIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Inativos</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>{stats.inactive}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'white', border: '1px solid rgba(20, 184, 166, 0.15)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <SecurityIcon sx={{ fontSize: 20, color: '#14b8a6' }} />
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Perfis</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>{Object.keys(stats.by_role).length}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Role Overview Cards */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {getAllRoles().map((r) => (
          <Chip
            key={r.value}
            icon={getRoleIcon(r.value)}
            label={`${r.label}: ${stats?.by_role[r.value] || 0}`}
            onClick={() => setRoleFilter(roleFilter === r.value ? '' : r.value)}
            sx={{
              bgcolor: roleFilter === r.value ? ROLE_COLORS[r.value].color : ROLE_COLORS[r.value].bg,
              color: roleFilter === r.value ? 'white' : ROLE_COLORS[r.value].color,
              border: `1px solid ${ROLE_COLORS[r.value].border}`,
              fontWeight: 600,
              fontSize: '0.8rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: roleFilter === r.value ? ROLE_COLORS[r.value].color : `${ROLE_COLORS[r.value].bg}dd`,
                transform: 'translateY(-1px)',
              },
              '& .MuiChip-icon': {
                color: roleFilter === r.value ? 'white' : ROLE_COLORS[r.value].color,
              },
            }}
          />
        ))}
      </Box>

      {/* Search and Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Buscar por nome ou email..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          size="small"
          sx={{
            flex: 1,
            minWidth: 250,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              '&:hover fieldset': { borderColor: '#14b8a6' },
              '&.Mui-focused fieldset': { borderColor: '#14b8a6' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            label="Status"
            sx={{
              borderRadius: 2,
              bgcolor: 'white',
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#14b8a6' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#14b8a6' },
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="true">Ativos</MenuItem>
            <MenuItem value="false">Inativos</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={() => { fetchUsers(); fetchStats(); }} sx={{ color: '#14b8a6' }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Users Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)',
          overflow: 'auto',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>Perfil</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>Profissional</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>Último Acesso</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.8rem' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={40} sx={{ color: '#14b8a6' }} />
                  <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>Carregando...</Typography>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#94a3b8' }}>Nenhum usuário encontrado</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const isCurrentUser = user.id === currentUser?.id;
                const colors = ROLE_COLORS[user.role] || ROLE_COLORS.assistant;

                return (
                  <TableRow
                    key={user.id}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(20, 184, 166, 0.03)' },
                      opacity: user.is_active ? 1 : 0.6,
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={user.avatar_url || undefined}
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: colors.color,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                          }}
                        >
                          {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                            {user.full_name}
                            {isCurrentUser && (
                              <Chip label="Você" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#e0f2fe', color: '#0284c7' }} />
                            )}
                          </Typography>
                          {user.phone && (
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{user.phone}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.85rem' }}>{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(user.role)}
                        label={getRoleLabel(user.role)}
                        size="small"
                        sx={{
                          bgcolor: colors.bg,
                          color: colors.color,
                          border: `1px solid ${colors.border}`,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          '& .MuiChip-icon': { color: colors.color },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {user.professional ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#475569' }}>
                            {user.professional.full_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {user.professional.registration_type} {user.professional.registration_number}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active ? 'Ativo' : 'Inativo'}
                        size="small"
                        sx={{
                          bgcolor: user.is_active ? '#f0fdf4' : '#fef2f2',
                          color: user.is_active ? '#16a34a' : '#dc2626',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          border: `1px solid ${user.is_active ? '#bbf7d0' : '#fecaca'}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {formatDate(user.last_login_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, user.id)}
                        sx={{ color: '#64748b' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination info */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          Mostrando {users.length} de {total} usuários
        </Typography>
        {total > 50 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              sx={{ textTransform: 'none', color: '#14b8a6' }}
            >
              Anterior
            </Button>
            <Button
              size="small"
              disabled={users.length < 50}
              onClick={() => setPage(page + 1)}
              sx={{ textTransform: 'none', color: '#14b8a6' }}
            >
              Próximo
            </Button>
          </Box>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.06)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const user = users.find(u => u.id === menuUserId);
            if (user) handleOpenDialog(user);
            handleMenuClose();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#14b8a6' }} /></ListItemIcon>
          <ListItemText primary="Editar" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const user = users.find(u => u.id === menuUserId);
            if (user) {
              setUnitsDialogUser(user);
              setUnitsDialogOpen(true);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon><BusinessIcon fontSize="small" sx={{ color: '#0d9488' }} /></ListItemIcon>
          <ListItemText primary="Unidades" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setResetPasswordUserId(menuUserId);
            setResetPasswordDialog(true);
            setNewPassword('');
            handleMenuClose();
          }}
        >
          <ListItemIcon><LockResetIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon>
          <ListItemText primary="Resetar Senha" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            const user = users.find(u => u.id === menuUserId);
            if (user) handleToggleStatus(user.id, user.is_active);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            {users.find(u => u.id === menuUserId)?.is_active
              ? <BlockIcon fontSize="small" sx={{ color: '#ef4444' }} />
              : <CheckCircleIcon fontSize="small" sx={{ color: '#22c55e' }} />
            }
          </ListItemIcon>
          <ListItemText primary={users.find(u => u.id === menuUserId)?.is_active ? 'Desativar' : 'Ativar'} />
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuUserId === currentUser?.id) {
              showSnackbar('Não é possível deletar seu próprio usuário.', 'warning');
            } else {
              setDeleteUserId(menuUserId);
              setDeleteConfirmDialog(true);
            }
            handleMenuClose();
          }}
          sx={{ color: '#ef4444' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText primary="Deletar" />
        </MenuItem>
      </Menu>

      {/* Create/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          pb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingUser ? <EditIcon sx={{ color: '#14b8a6' }} /> : <PersonAddIcon sx={{ color: '#14b8a6' }} />}
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                label="Nome Completo"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                fullWidth
                required
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                fullWidth
                required
                type="email"
                size="small"
              />
            </Grid>
            {!editingUser && (
              <Grid item xs={12}>
                <TextField
                  label="Senha"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  fullWidth
                  required
                  type={showPassword ? 'text' : 'password'}
                  size="small"
                  helperText="Mínimo 6 caracteres. O usuário poderá alterar após o primeiro login."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Perfil de Acesso</InputLabel>
                <Select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as SystemRole })}
                  label="Perfil de Acesso"
                >
                  {getAllRoles().map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRoleIcon(r.value)}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.label}</Typography>
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.7rem' }}>{r.description}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                fullWidth
                size="small"
                placeholder="(11) 99999-9999"
              />
            </Grid>
            {(form.role === 'professional' || form.role === 'assistant') && (
              <Grid item xs={12}>
                <Autocomplete
                  options={professionals}
                  getOptionLabel={(option) => `${option.full_name} - ${option.registration_type} ${option.registration_number}`}
                  value={professionals.find(p => p.id === form.professional_id) || null}
                  onChange={(_, value) => setForm({ ...form, professional_id: value?.id || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vincular a Profissional"
                      size="small"
                      helperText="Vincule este usuário a um profissional cadastrado na clínica"
                    />
                  )}
                  noOptionsText="Nenhum profissional encontrado"
                />
              </Grid>
            )}
          </Grid>

          {/* Role description */}
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: ROLE_COLORS[form.role]?.bg || '#f8fafc', border: `1px solid ${ROLE_COLORS[form.role]?.border || '#e2e8f0'}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {getRoleIcon(form.role)}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ROLE_COLORS[form.role]?.color || '#475569' }}>
                {ROLE_LABELS[form.role]}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
              {ROLE_DESCRIPTIONS[form.role]}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' },
            }}
          >
            {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialog}
        onClose={() => setResetPasswordDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockResetIcon sx={{ color: '#f59e0b' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Resetar Senha</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Defina uma nova senha para este usuário. Ele será solicitado a alterar no próximo login.
          </Typography>
          <TextField
            label="Nova Senha"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            type={showNewPassword ? 'text' : 'password'}
            size="small"
            helperText="Mínimo 6 caracteres"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNewPassword(!showNewPassword)}>
                    {showNewPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResetPasswordDialog(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleResetPassword}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#f59e0b',
              '&:hover': { bgcolor: '#d97706' },
            }}
          >
            Resetar Senha
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon sx={{ color: '#ef4444' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Confirmar Exclusão</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Esta ação não pode ser desfeita. Todas as sessões do usuário serão encerradas.
          </Alert>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Tem certeza que deseja deletar o usuário <strong>{users.find(u => u.id === deleteUserId)?.full_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirmDialog(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#ef4444',
              '&:hover': { bgcolor: '#dc2626' },
            }}
          >
            Deletar Usuário
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity Log Dialog */}
      <Dialog
        open={activityLogDialog}
        onClose={() => setActivityLogDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: '#14b8a6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Logs de Atividade</Typography>
          </Box>
          <IconButton onClick={() => setActivityLogDialog(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {activityLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={40} sx={{ color: '#14b8a6' }} />
            </Box>
          ) : activityLogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <HistoryIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
              <Typography variant="body1" sx={{ color: '#94a3b8', mt: 1 }}>Nenhum log encontrado</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>Data</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>Usuário</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>Ação</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>Recurso</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>IP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityLogs.map((log, index) => {
                    const userName = log.system_users?.full_name || log.user_id?.substring(0, 8) || '—';
                    const actionLabels: Record<string, string> = {
                      login: 'Login',
                      logout: 'Logout',
                      create_user: 'Criou Usuário',
                      update_user: 'Atualizou Usuário',
                      delete_user: 'Deletou Usuário',
                      reset_password: 'Resetou Senha',
                      change_password: 'Alterou Senha',
                      activate_user: 'Ativou Usuário',
                      deactivate_user: 'Desativou Usuário',
                    };
                    return (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: 'rgba(20,184,166,0.03)' } }}>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {formatDate(log.created_at)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#475569', fontWeight: 600 }}>
                          {userName}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={actionLabels[log.action] || log.action}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: log.action.includes('delete') || log.action.includes('deactivate')
                                ? '#fef2f2'
                                : log.action.includes('create') || log.action.includes('activate')
                                  ? '#f0fdf4'
                                  : '#f8fafc',
                              color: log.action.includes('delete') || log.action.includes('deactivate')
                                ? '#dc2626'
                                : log.action.includes('create') || log.action.includes('activate')
                                  ? '#16a34a'
                                  : '#475569',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          {log.resource_type || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          {log.ip_address || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de unidades vinculadas (multi-unidade) */}
      <UserUnitsDialog
        open={unitsDialogOpen}
        userId={unitsDialogUser?.id || null}
        userName={unitsDialogUser?.full_name || null}
        onClose={() => { setUnitsDialogOpen(false); setUnitsDialogUser(null); }}
        onSaved={() => { fetchUsers(); }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
